import React, { useCallback, useEffect, useState } from "react";
import axios from "../axiosConfig";
import { ADMIN_EMAILS, canShowAdminAccess } from "../adminAccess";
import { useToast } from "../context/ToastContext";

const API_BASE = "/api";

export default function Access() {
  const toast = useToast();
  const [emails, setEmails] = useState([]);
  const [newEmail, setNewEmail] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const allowedHere = canShowAdminAccess();

  const loadEmails = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API_BASE}/admin/access`);
      setEmails(Array.isArray(res.data?.emails) ? res.data.emails : []);
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 403) {
        setError(
          typeof detail === "string"
            ? detail
            : "Admin access is only available to authorized accounts on the management address."
        );
      } else {
        setError("Could not load the access list.");
      }
      setEmails([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadEmails();
  }, [loadEmails]);

  const addEmail = async (event) => {
    event.preventDefault();
    const email = newEmail.trim().toLowerCase();
    if (!email) return;
    setSaving(true);
    try {
      const res = await axios.post(`${API_BASE}/admin/access`, { email });
      setEmails(Array.isArray(res.data?.emails) ? res.data.emails : []);
      setNewEmail("");
      toast.success(`Granted access to ${email}`);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not add that email.");
    } finally {
      setSaving(false);
    }
  };

  const removeEmail = async (email) => {
    if (ADMIN_EMAILS.has(email)) return;
    setSaving(true);
    try {
      const res = await axios.delete(`${API_BASE}/admin/access`, { params: { email } });
      setEmails(Array.isArray(res.data?.emails) ? res.data.emails : []);
      toast.success(`Removed ${email}`);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not remove that email.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="w-full">
      <div className="mb-8">
        <h1 className="text-2xl font-semibold tracking-tight text-slate-900 dark:text-white sm:text-3xl">
          Access
        </h1>
        <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
          Grant or revoke wiki login emails. Changes apply immediately without a backend restart.
        </p>
        {!allowedHere && (
          <p className="mt-2 text-sm text-amber-800 dark:text-amber-200">
            This page is only available when you open the wiki at a 172.30.4.x management address.
          </p>
        )}
      </div>

      {error && (
        <div
          className="mb-6 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800 dark:border-red-800 dark:bg-red-950/40 dark:text-red-200"
          role="alert"
        >
          {error}
        </div>
      )}

      {loading && !error && (
        <p className="text-sm text-slate-600 dark:text-slate-400">Loading access list…</p>
      )}

      {!loading && !error && (
        <div className="space-y-6">
          <form
            onSubmit={addEmail}
            className="flex flex-col gap-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-700 dark:bg-slate-900/80 sm:flex-row sm:items-end"
          >
            <label className="min-w-0 flex-1 text-sm font-medium text-slate-700 dark:text-slate-200">
              Add email
              <input
                type="email"
                value={newEmail}
                onChange={(event) => setNewEmail(event.target.value)}
                placeholder="user@radware.com"
                className="mt-1 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 dark:border-slate-600 dark:bg-slate-950 dark:text-slate-100"
                required
              />
            </label>
            <button
              type="submit"
              disabled={saving}
              className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50 dark:bg-blue-600 dark:hover:bg-blue-500"
            >
              Grant access
            </button>
          </form>

          <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-900/80">
            <div className="border-b border-slate-200 px-4 py-3 text-sm font-medium text-slate-700 dark:border-slate-700 dark:text-slate-200">
              {emails.length} allowed {emails.length === 1 ? "email" : "emails"}
            </div>
            <ul className="divide-y divide-slate-200 dark:divide-slate-700">
              {emails.map((email) => {
                const isAdmin = ADMIN_EMAILS.has(email);
                return (
                  <li
                    key={email}
                    className="flex items-center justify-between gap-3 px-4 py-3 text-sm"
                  >
                    <span className="min-w-0 truncate text-slate-800 dark:text-slate-100">
                      {email}
                      {isAdmin && (
                        <span className="ml-2 rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                          admin
                        </span>
                      )}
                    </span>
                    <button
                      type="button"
                      onClick={() => removeEmail(email)}
                      disabled={saving || isAdmin}
                      className="shrink-0 rounded-md border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-800"
                    >
                      Remove
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}
